import { Button } from '@/components/ui/button'
import { useState, useRef, useEffect } from 'react'
import Header from '@/components/Header'
import authBg from '@/assets/Landing/authbg.svg'
import { useLocation, useNavigate } from 'react-router'
import { useAuth } from '@/context/AuthContext'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'

export default function VerifyAccountPage() {
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [phoneNumber, setPhoneNumber] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [showSuccess, setShowSuccess] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const navigate = useNavigate()
  const location = useLocation()
  const { sendOtp, verifyOtp, loading } = useAuth()

  useEffect(() => {
    const phoneFromState = (location.state as { phoneNumber?: string })?.phoneNumber
    if (phoneFromState) {
      setPhoneNumber(phoneFromState)
      handleResendOTP(phoneFromState)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [countdown])

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return // Only allow digits

    const newOtp = [...otp]
    newOtp[index] = value.slice(-1) // Take only the last digit
    setOtp(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 6)
    if (!/^\d+$/.test(pastedData)) return

    const newOtp = [...otp]
    pastedData.split('').forEach((char, index) => {
      if (index < 6) newOtp[index] = char
    })
    setOtp(newOtp)

    // Focus the next empty input or the last one
    const nextEmptyIndex = newOtp.findIndex((val) => !val)
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus()
    } else {
      inputRefs.current[5]?.focus()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const otpCode = otp.join('')
    if (otpCode.length !== 6) return
    setError(null)
    setMessage(null)

    verifyOtp({ phoneNumber, otp: otpCode })
      .then(res => {
        setShowSuccess(true)
        if (res.needsPassword) {
          navigate('/create-password', { state: { phoneNumber, userId: res.userId } })
        } else {
          // User already has a password, redirect to login
          navigate('/login', { state: { phoneNumber } })
        }
      })
      .catch(err => {
        setError((err as Error)?.message || 'Invalid or expired OTP')
      })
  }

  const handleResendOTP = (phone?: string) => {
    const targetPhone = phone || phoneNumber
    if (!targetPhone) {
      setError('Enter your phone number first')
      return
    }
    setError(null)
    setMessage(null)
    sendOtp(targetPhone)
      .then(() => {
        setMessage('OTP sent to your phone')
        setCountdown(60)
        setOtp(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
      })
      .catch(err => setError((err as Error)?.message || 'Unable to send OTP'))
  }

  const isOtpComplete = otp.every(digit => digit !== '')

  return (
    <div className="min-h-screen bg-white flex flex-col w-full">
      <Header />

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:grid lg:grid-rows-2 w-full">
        {/* Left Section - Form */}
        <div className="flex flex-col lg:flex-row items-center justify-center px-6 lg:px-16 py-12 lg:py-16 w-full">
          <div className="w-full space-y-8 flex flex-col lg:flex-row gap-8">
            <div className="space-y-3 flex flex-col justify-start items-start lg:w-1/2">
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900">
                Verify Your Account
              </h2>
              <div className="space-y-1">
                <p className="text-gray-600 text-base lg:text-lg xl:text-xl text-start">
                  Type the 6-digit code from your email. This helps keep your account secure.
                </p>
              </div>
            </div>

           <div className='flex flex-row items-center justify-center w-full lg:w-1/2'>
           <form onSubmit={handleSubmit} className="space-y-6 w-full max-w-md">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm text-start">
                  {error}
                </div>
              )}
              {message && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm text-start">
                  {message}
                </div>
              )}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 text-start">
                  Phone Number (E.164)
                </label>
                <PhoneNumberInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  placeholder="Phone number"
                  required
                  disabled={loading}
                  containerClassName="px-4 py-3 border-gray-300"
                  inputClassName="py-0"
                />
              </div>

              {/* OTP Input Fields */}
              <div className="flex gap-2 justify-between">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="w-12 h-14 text-center text-xl font-semibold rounded-lg border border-gray-300 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 text-gray-900"
                    required
                  />
                ))}
              </div>

              {/* Log In Button */}
              <Button
                type="submit"
                disabled={!isOtpComplete}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-lg py-3 text-base font-medium transition-all disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Log In
              </Button>

              {/* Resend OTP */}
              <div className="text-center text-sm">
                {countdown > 0 ? (
                  <p className="text-gray-600">
                    Didn't get the code?{' '}
                    <span className="text-orange-500 font-medium">
                      Resend OTP in 00:{countdown.toString().padStart(2, '0')}
                    </span>
                  </p>
                ) : (
                  <p className="text-gray-600">
                    Didn't get the code?{' '}
                    <button
                      type="button"
                      onClick={() => handleResendOTP()}
                      className="text-orange-500 hover:text-orange-600 font-medium underline"
                      disabled={loading}
                    >
                      Resend OTP
                    </button>
                  </p>
                )}
              </div>
            </form>
           </div>
          </div>
        </div>

        {/* Right Section - Illustration */}
        <div className="lg:flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100/50 relative overflow-hidden rounded-tr-[32px] rounded-tl-[32px] mx-2 sm:mx-4 lg:mx-20 h-48 sm:h-64 lg:h-auto min-h-[200px] sm:min-h-[300px] lg:min-h-[400px]">
          <img 
            src={authBg} 
            alt="Authentication illustration" 
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          
          {/* Success Toast */}
          {showSuccess && (
            <div className="absolute bottom-8 left-8 bg-white rounded-lg shadow-lg p-4 flex items-start gap-3 animate-in slide-in-from-bottom-5 duration-300">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 text-sm">Congratulations!</p>
                <p className="text-gray-600 text-xs">Your password has been successfully reset</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
